/**
 * @fileoverview Componente Organismo para mostrar integraciones de cliente (Shopify, Hubspot) en el módulo Inbox.
 * @module CustomerIntegrationsMC
 * @category Component - Organism
 * @since 3.1.0
 * @version 3.1.0
 *
 * @description
 * Componente Organismo optimizado que muestra el estado de integraciones de un cliente (Shopify, Hubspot) en el sidebar derecho del Inbox.
 * Implementa arquitectura de Atomic Design, uso de hooks especializados y optimización de performance con React.memo.
 *
 * **Características:**
 * - Visualiza integraciones activas/desactivadas para el cliente
 * - Soporte para Shopify y Hubspot (con botones de acción)
 * - Traducción internacionalizada (i18n)
 * - Uso de imágenes y componentes atómicos
 * - Memoización para evitar renders innecesarios
 *
 * **Atomic Design:**
 * - Nivel: Organism
 * - Composición: Utiliza molécula CustomerIntegrationItemMC
 * - Dependencias: useCustomerIntegrationsActions (Actions Layer), useTranslation (i18n)
 *
 * @typedef {Object} CustomerIntegrationsMCProps
 * @property {Object} customerData - Objeto con datos del cliente, debe incluir propiedad 'customer' con integraciones
 * @property {Function} fetchCustomerData - Función para recargar datos del cliente tras acciones
 *
 * @param {CustomerIntegrationsMCProps} props - Propiedades del componente
 * @param {Object} props.customerData - Datos del cliente (incluye integraciones)
 * @param {Function} props.fetchCustomerData - Callback para recargar datos del cliente
 *
 * @returns {JSX.Element} Elemento JSX renderizado con las integraciones del cliente
 *
 * @example
 * <CustomerIntegrationsMC customerData={customerData} fetchCustomerData={loadCustomer} />
 *
 * @see {@link CustomerIntegrationItemMC} Componente hijo para renderizar cada integración
 * @see {@link useCustomerIntegrationsActions} Hook de Actions Layer para lógica de botones Hubspot
 */
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import shopify from 'images/integrations/shopify-2.png';
import hubspot from 'images/integrations/hubspot-img.png';
import { CustomerIntegrationItemMC } from './components';
import useCustomerIntegrationsActions from '../../hooks/inbox/actions/useCustomerIntegrationsActions';

const CustomerIntegrationsMC = memo(({ customerData, fetchCustomerData }) => {
  /**
   * Extrae los datos de integraciones del objeto customer.
   * @type {string|undefined}
   */
  const { shopify_id, hs_active, hs_id } = customerData || {};

  /**
   * Hook de traducción internacionalizada para el namespace InboxMC.
   */
  const { t } = useTranslation('InboxMC');

  /**
   * Obtiene los botones de acción para Hubspot según el estado de integración.
   * @type {Object}
   */
  const { hubspotButtons } = useCustomerIntegrationsActions({
    customerId: customerData?.id,
    hs_active,
    fetchCustomerData,
    hs_id,
    t
  });

  return (
    <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
      <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
        <div className="tw-text-sm tw-font-medium tw-text-gray-950 tw-mb-2">
          {t('integrations.title')}
        </div>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          {shopify_id && (
            <CustomerIntegrationItemMC description={t('integrations.shopify_connected')} iconSrc={shopify} />
          )}

          <CustomerIntegrationItemMC
            description={
              !ENV.HUBSPOT_INTEGRATED
                ? t('integrations.hubspot_not_integrated')
                : hs_active
                  ? t('integrations.hubspot_connected')
                  : t('integrations.hubspot_disconnected')
            }
            iconSrc={hubspot}
            buttons={hubspotButtons}
          />
        </div>
      </div>
    </div>
  );
});

CustomerIntegrationsMC.displayName = 'CustomerIntegrationsMC';

export default CustomerIntegrationsMC;